import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { EmptyState, ErrorState, LoadingSkeleton } from "@/components/states";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { generateQuiz, submitQuizAttempt } from "@/lib/study.functions";
import { Sparkles, Loader2, Check, X, Trophy } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { DsaText } from "@/components/DsaText";
import { AssistantMessage } from "@/components/AssistantMessage";
import { MediaAttachButton } from "@/components/MediaAttachButton";

export const Route = createFileRoute("/_app/quiz")({
  head: () => ({
    meta: [
      { title: "Quiz — Lumen" },
      { name: "description", content: "Quiz a scelta multipla generati dall'IA con spiegazioni e cronologia dei tentativi." },
    ],
  }),
  component: QuizPage,
});

type Question = { q: string; options: string[]; answer: number; explain: string };
type Quiz = { id: string; title: string; subject_name: string | null; questions: Question[]; created_at: string };

function QuizPage() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<Quiz | null>(null);
  return (
    <div className="space-y-5 px-5 pt-8">
      <PageHeader
        tone="sage"
        icon={<Trophy className="h-5 w-5" />}
        kicker={t("quiz.eyebrow")}
        title={t("quiz.title")}
        subtitle={t("quiz.subtitle")}
      />
      {current ? (
        <QuizRunner quiz={current} onExit={() => setCurrent(null)} />
      ) : (
        <Tabs defaultValue="list">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="list">{t("quiz.tabMine")}</TabsTrigger>
            <TabsTrigger value="create">{t("quiz.tabCreate")}</TabsTrigger>
          </TabsList>
          <TabsContent value="list" className="mt-4"><QuizList onOpen={setCurrent} /></TabsContent>
          <TabsContent value="create" className="mt-4"><CreateQuiz onCreated={setCurrent} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function QuizList({ onOpen }: { onOpen: (q: Quiz) => void }) {
  const { t } = useTranslation();
  const quizzesQ = useQuery({
    queryKey: ["quizzes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quizzes")
        .select("id,title,subject_name,questions,created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Quiz[];
    },
  });
  const quizzes = quizzesQ.data ?? [];
  if (quizzesQ.isPending) return <LoadingSkeleton rows={2} />;
  if (quizzesQ.isError) return <ErrorState error={quizzesQ.error} onRetry={() => quizzesQ.refetch()} />;
  if (quizzes.length === 0)
    return <EmptyState description={t("quiz.emptyList")} />;
  return (
    <div className="space-y-2">
      {quizzes.map((q) => (
        <Card key={q.id} className="card-soft flex items-center justify-between border-0 p-4">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{q.title}</p>
            <p className="text-xs text-muted-foreground">{t("quiz.questionsCount", { count: q.questions.length })}{q.subject_name ? ` · ${q.subject_name}` : ""}</p>
          </div>
          <Button size="sm" onClick={() => onOpen(q)}>{t("quiz.start")}</Button>
        </Card>
      ))}
    </div>
  );
}

function CreateQuiz({ onCreated }: { onCreated: (q: Quiz) => void }) {
  const { t } = useTranslation();
  const [topic, setTopic] = useState("");
  const [source, setSource] = useState("");
  const [subject, setSubject] = useState("");
  const [difficulty, setDifficulty] = useState<"base" | "medio" | "avanzato">("medio");
  const [count, setCount] = useState(6);
  const [attachedName, setAttachedName] = useState<string | null>(null);
  const runGen = useServerFn(generateQuiz);

  const gen = useMutation({
    mutationFn: async () =>
      runGen({ data: { topic, sourceText: source || undefined, subject: subject || undefined, count, difficulty } }),
    onSuccess: (r) => {
      toast.success(t("quiz.readyToast", { title: r.title }));
      onCreated({
        id: r.id, title: r.title, subject_name: subject || null,
        questions: r.questions as Question[], created_at: new Date().toISOString(),
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="card-soft space-y-3 border-0 p-4">
      <div className="space-y-1.5">
        <Label htmlFor="qz-topic">{t("quiz.topicLabel")}</Label>
        <Input id="qz-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("quiz.topicPlaceholder")} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="qz-subject">{t("quiz.subjectLabel")}</Label>
          <Input id="qz-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("quiz.subjectPlaceholder")} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("quiz.difficultyLabel")}</Label>
          <Select value={difficulty} onValueChange={(v) => setDifficulty(v as typeof difficulty)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="base">{t("quiz.difficultyBase")}</SelectItem>
              <SelectItem value="medio">{t("quiz.difficultyMedium")}</SelectItem>
              <SelectItem value="avanzato">{t("quiz.difficultyAdvanced")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="qz-source">{t("quiz.sourceLabel")}</Label>
        <Textarea
          id="qz-source"
          value={source}
          onChange={(e) => { setSource(e.target.value); setAttachedName(null); }}
          rows={4}
          placeholder={t("quiz.sourcePlaceholder")}
        />
        <MediaAttachButton
          attachedName={attachedName}
          onClear={() => { setAttachedName(null); setSource(""); }}
          onText={(text, name) => {
            setSource(text);
            setAttachedName(name);
            if (!topic.trim()) setTopic(name.replace(/\.[^.]+$/, "").slice(0, 60));
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label>{t("quiz.countLabel", { count })}</Label>
        <input type="range" min={3} max={15} value={count} onChange={(e) => setCount(Number(e.target.value))} className="w-full" />
      </div>
      <Button
        onClick={() => gen.mutate()}
        disabled={gen.isPending || topic.trim().length < 3}
        className="w-full rounded-xl"
      >
        {gen.isPending ? (
          <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("quiz.generating")}</>
        ) : (
          <><Sparkles className="mr-1.5 h-4 w-4" /> {t("quiz.generateWithAi")}</>
        )}
      </Button>
    </Card>
  );
}

function QuizRunner({ quiz, onExit }: { quiz: Quiz; onExit: () => void }) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<number[]>(Array(quiz.questions.length).fill(-1));
  const [locked, setLocked] = useState<boolean[]>(Array(quiz.questions.length).fill(false));
  const [result, setResult] = useState<{ score: number; total: number; perfect: boolean } | null>(null);
  const runSubmit = useServerFn(submitQuizAttempt);

  const q = quiz.questions[idx];
  const chosen = answers[idx];
  const isLocked = locked[idx];

  function choose(i: number) {
    if (isLocked) return;
    const next = [...answers]; next[idx] = i; setAnswers(next);
    const nl = [...locked]; nl[idx] = true; setLocked(nl);
  }

  const submit = useMutation({
    mutationFn: async () => runSubmit({ data: { quizId: quiz.id, answers } }),
    onSuccess: (r) => setResult(r),
    onError: (e: Error) => toast.error(e.message),
  });

  if (result) {
    return (
      <Card className="card-soft space-y-3 border-0 p-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Trophy className="h-7 w-7" />
        </div>
        <h2 className="text-2xl font-semibold">{result.score} / {result.total}</h2>
        <p className="text-sm text-muted-foreground">
          {result.perfect ? t("quiz.perfectResult") : t("quiz.keepGoing")}
        </p>
        <div className="mt-3 space-y-2 text-left">
          {quiz.questions.map((qq, i) => {
            const ok = answers[i] === qq.answer;
            return (
              <div key={i} className={`rounded-lg p-2 text-xs ${ok ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
                <div className="mb-0.5 flex items-center gap-1 font-medium">
                  {ok ? <Check className="h-3 w-3 shrink-0" /> : <X className="h-3 w-3 shrink-0" />}
                  <AssistantMessage content={qq.q} className="[&_p]:mb-0" />
                </div>
                <AssistantMessage content={qq.explain ?? ""} className="text-muted-foreground" />
              </div>
            );
          })}
        </div>
        <Button onClick={onExit} variant="outline" className="w-full">{t("quiz.backToQuizzes")}</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t("quiz.questionOf", { current: idx + 1, total: quiz.questions.length })}</span>
        <Badge variant="secondary">{quiz.title}</Badge>
      </div>
      <Card className="card-soft border-0 p-5">
        <AssistantMessage content={q.q} className="text-base font-medium leading-relaxed" />
        <div className="mt-4 space-y-2">
          {q.options.map((opt, i) => {
            const correct = isLocked && i === q.answer;
            const wrong = isLocked && chosen === i && i !== q.answer;
            return (
              <button
                key={i}
                onClick={() => choose(i)}
                disabled={isLocked}
                className={`flex w-full items-center gap-2 rounded-xl border p-3 text-left text-sm transition ${
                  correct
                    ? "border-emerald-500 bg-emerald-500/10"
                    : wrong
                    ? "border-rose-500 bg-rose-500/10"
                    : chosen === i
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted"
                }`}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold">
                  {String.fromCharCode(65 + i)}
                </span>
                <AssistantMessage content={opt} className="flex-1 [&_p]:mb-0" />
                {correct && <Check className="h-4 w-4 text-emerald-600" />}
                {wrong && <X className="h-4 w-4 text-rose-600" />}
              </button>
            );
          })}
        </div>
        {isLocked && (
          <AssistantMessage
            content={q.explain ?? ""}
            className="mt-3 rounded-lg bg-muted/60 p-2 text-xs text-muted-foreground"
          />
        )}
      </Card>
      <div className="flex gap-2">
        <Button variant="ghost" className="flex-1" onClick={onExit}>{t("quiz.exit")}</Button>
        {idx < quiz.questions.length - 1 ? (
          <Button className="flex-1" disabled={!isLocked} onClick={() => setIdx((i) => i + 1)}>
            {t("quiz.next")}
          </Button>
        ) : (
          <Button className="flex-1" disabled={!isLocked || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? t("quiz.submitting") : t("quiz.finish")}
          </Button>
        )}
      </div>
    </div>
  );
}
